import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { consentService } from '../../../services/consentService';

const DiscussionPanel = ({ consentWorkflow, onRequestConsultation }) => {
  const [newMessage, setNewMessage] = useState('');
  const [consultationQuestions, setConsultationQuestions] = useState('');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [sending, setSending] = useState(false);

  const discussions = consentWorkflow?.discussions || [];

  const handleSendMessage = async () => {
    if (!newMessage?.trim()) return;

    try {
      setSending(true);
      await consentService?.addDiscussionMessage(
        consentWorkflow?.id,
        newMessage,
        false
      );
      setNewMessage('');
      window.location?.reload();
    } catch (err) {
      alert(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleRequestConsultation = async () => {
    if (!consultationQuestions?.trim()) {
      alert('Please describe what you would like to discuss.');
      return;
    }

    try {
      setSending(true);
      await onRequestConsultation(consultationQuestions);
      setConsultationQuestions('');
      setShowConsultationForm(false);
    } catch (err) {
      alert(err?.message || 'Failed to request consultation');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Discussion Instructions */}
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="MessageSquare" size={20} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Communicate with Your Provider</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Use this secure messaging system to ask questions about your treatment plan, discuss concerns, or request a consultation appointment. Your provider will respond within 1-2 business days.
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>All messages are HIPAA-compliant and securely encrypted</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>You will receive an email notification when your provider responds</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>For urgent matters, please call the office directly</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Consultation Request */}
      {consentWorkflow?.requiresConsultation ? (
        <div className="bg-warning/5 rounded-lg border border-warning/20 p-4">
          <div className="flex items-start gap-3">
            <Icon name="Calendar" size={20} className="text-warning flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-2">Consultation Requested</h4>
              <p className="text-sm text-muted-foreground mb-3">
                You have requested a consultation to discuss this treatment plan. Your provider has been notified and will contact you to schedule an appointment.
              </p>
              {consentWorkflow?.patientQuestions && (
                <div className="bg-background rounded-lg p-3 mt-3">
                  <p className="text-xs text-muted-foreground mb-1">Your Questions:</p>
                  <p className="text-sm text-foreground">{consentWorkflow?.patientQuestions}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        !showConsultationForm && (
          <Button 
            variant="outline" 
            size="lg" 
            iconName="Calendar" 
            onClick={() => setShowConsultationForm(true)}
            className="w-full"
          >
            Request Consultation Appointment
          </Button>
        )
      )}
      {/* Consultation Form */}
      {showConsultationForm && !consentWorkflow?.requiresConsultation && (
        <div className="bg-background rounded-lg border border-border p-4">
          <h4 className="font-semibold text-foreground mb-3">Request Consultation</h4>
          <textarea
            value={consultationQuestions}
            onChange={(e) => setConsultationQuestions(e?.target?.value)}
            placeholder="Please describe what you would like to discuss during the consultation..."
            className="w-full min-h-[120px] p-3 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3"
          />
          <div className="flex gap-2">
            <Button 
              variant="default" 
              size="sm" 
              iconName="Send" 
              onClick={handleRequestConsultation}
              disabled={sending}
            >
              Send Request
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowConsultationForm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Message Thread */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="MessageCircle" size={20} className="text-primary" />
          Message Thread
        </h3>
        
        {discussions?.length > 0 ? (
          <div className="space-y-3 mb-4">
            {discussions?.map(msg => (
              <div 
                key={msg?.id} 
                className={`flex gap-3 ${
                  msg?.isProvider ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon 
                    name={msg?.isProvider ? 'Stethoscope' : 'User'} 
                    size={18} 
                    className="text-primary" 
                  />
                </div>
                <div className={`flex-1 max-w-[80%] ${
                  msg?.isProvider ? 'text-left' : 'text-right'
                }`}>
                  <div className={`inline-block rounded-lg p-3 ${
                    msg?.isProvider 
                      ? 'bg-background border border-border' :'bg-primary/10 border border-primary/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-foreground">
                        {msg?.sender?.full_name || (msg?.isProvider ? 'Provider' : 'You')}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{msg?.messageText}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-muted/50 rounded-lg border border-border p-8 text-center mb-4">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No messages yet. Start a conversation with your provider.</p>
          </div>
        )}

        {/* New Message Input */}
        <div className="bg-background rounded-lg border border-border p-4">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            placeholder="Type your message or question here..."
            className="w-full min-h-[100px] p-3 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Your provider will be notified via email when you send a message.
            </p>
            <Button 
              variant="default" 
              size="sm" 
              iconName="Send" 
              onClick={handleSendMessage}
              disabled={sending || !newMessage?.trim()}
            >
              Send Message
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Questions */}
      <div className="bg-background rounded-lg border border-border p-4">
        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="HelpCircle" size={18} className="text-primary" />
          Common Questions
        </h4>
        <div className="space-y-2">
          {[
            'What are the payment options available?',
            'How long will each appointment take?',
            'What should I expect during recovery?',
            'Are there any dietary restrictions?'
          ]?.map((question, idx) => (
            <button
              key={idx}
              onClick={() => setNewMessage(question)}
              className="w-full text-left px-3 py-2 bg-card hover:bg-muted rounded-lg text-sm text-foreground transition-colors"
            >
              {question}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DiscussionPanel;